import { logout } from '@/utils/authority';
import { getValidAccessToken } from '@/utils/refreshToken';
import message from 'antd/es/message';
import type { AxiosResponse } from 'axios';
import Axios from 'axios';
import queryString from 'query-string';

const PUBLIC_ROUTES = ['/auth/sso/azure', '/auth/signup/azure'];

export const apiClient = Axios.create({
  baseURL: '/rest',
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
  paramsSerializer: (params) => queryString.stringify(params),
});

export class ResponseError extends Error {
  response?: AxiosResponse;

  jsonResponse: any;

  textResponse: string;

  displayMessage: string;

  status?: number;

  constructor(msg: string, response?: AxiosResponse) {
    super(msg);
    this.response = response;
    this.jsonResponse = {};
    this.textResponse = '';
    this.displayMessage = msg;
  }
}

const getErrorDisplayMessage = (data: any): string | undefined => {
  if (!data || typeof data !== 'object') {
    return undefined;
  }

  if (typeof data.message === 'string' && data.message.trim()) {
    return data.message;
  }

  if (typeof data.detail === 'string' && data.detail.trim()) {
    return data.detail;
  }

  if (typeof data.title === 'string' && data.title.trim()) {
    return data.title;
  }

  if (Array.isArray(data.errors) && data.errors.length > 0) {
    const [firstError] = data.errors;
    if (typeof firstError === 'string' && firstError.trim()) {
      return firstError;
    }
  }

  if (typeof data.errors === 'string' && data.errors.trim()) {
    return data.errors;
  }

  return undefined;
};

apiClient.interceptors.request.use((config) => {
  // No extra configuration for absolute URLs (i.e.: a file in S3)
  if (config.url!.charAt(0) !== '/') {
    return config;
  }

  if (config.url === '/auth/refresh' || PUBLIC_ROUTES.includes(config.url!)) {
    return config;
  }

  return getValidAccessToken()
    .then((accessToken) => {
      config.headers = config.headers ?? {};

      config.headers.Authorization = `Bearer ${accessToken}`;
      return config;
    })
    .catch((error: string) => {
      return Promise.reject(new Axios.Cancel(error));
    });
});

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    let errorText = `Unhandled status: ${error.response?.status}`;

    if (error.response?.status === 401 && !PUBLIC_ROUTES.includes(error?.config?.url)) {
      logout();
      errorText = 'The username or password you entered is incorrect.';
    }
    if (error.response?.status === 500) {
      errorText = 'Internal Server Error';
    }

    if (error.response?.status === 400) {
      errorText = 'Bad Request';
    }

    if (error.response?.status === 403) {
      errorText = 'Missing permission';
    }

    if (error.response?.status === 504) {
      errorText = 'Gateway timeout';
      message.error({
        content: 'Error communicating with remote server. Please check your internet connection.',
        // cspell:ignore NETGONE
        key: 'NETGONE',
      });
    }

    const responseError = new ResponseError(errorText, error.response);

    responseError.status = error.response?.status;
    if (error.response?.data instanceof Object) {
      responseError.jsonResponse = error.response!.data;
      const displayMessage = getErrorDisplayMessage(responseError.jsonResponse);
      if (displayMessage) {
        responseError.displayMessage = displayMessage;
      }
    } else {
      responseError.textResponse = error.response?.data as string;
    }
    throw responseError;
  },
);
